import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer, Copy, Loader2, Award } from "lucide-react";
import { LOGO_URL } from "../constants";
import { api } from "../api/client";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return d; }
};

export default function CertificateViewer() {
  const { certId } = useParams();
  const [cert,    setCert]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    api.get(`/certificates/${certId}`)
      .then(setCert)
      .catch(() => setError("Certificate not found"))
      .finally(() => setLoading(false));
  }, [certId]);

  const copyVerify = () => {
    navigator.clipboard.writeText(`${window.location.origin}/verify/${certId}`);
    toast.success("Verification link copied");
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0E0F12] flex items-center justify-center text-gray-500">
      <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading certificate…
    </div>
  );

  if (error || !cert) return (
    <div className="min-h-screen bg-[#0E0F12] flex items-center justify-center text-white">
      <div className="text-center">
        <Award className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">{error || "Certificate not found"}</p>
        <Link to="/certificates" className="mt-4 inline-block text-lime text-sm">← Back to certificates</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0E0F12] text-white">
      {/* Nav — hidden on print */}
      <div className="no-print sticky top-0 z-30 bg-[#0E0F12]/90 backdrop-blur border-b border-white/[0.08]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/certificates" data-testid="back-cert-list"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> All certificates
          </Link>
          <div className="flex gap-2">
            <button onClick={copyVerify} data-testid="share-cert"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm font-semibold hover:bg-white/5 transition-colors">
              <Copy className="w-3.5 h-3.5" /> Copy verify link
            </button>
            <button onClick={() => window.print()} data-testid="print-cert"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-lime text-[#0E0F12] text-sm font-bold hover:bg-[#d8ff6a] transition-colors">
              <Printer className="w-3.5 h-3.5" /> Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      {/* Certificate */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div
          className="print-cert relative overflow-hidden text-center"
          style={{
            aspectRatio: "1.414 / 1",
            background: "linear-gradient(135deg, #fdfaf3 0%, #f5eeda 100%)",
            color: "#1a1a1a",
            borderRadius: 16,
          }}
        >
          {/* Double border */}
          <div className="absolute pointer-events-none" style={{ inset: 14, border: "2px double rgba(124,92,255,0.5)", borderRadius: 8 }} />
          <div className="absolute pointer-events-none" style={{ inset: 20, border: "1px solid rgba(124,92,255,0.2)", borderRadius: 6 }} />

          {/* Content */}
          <div className="relative h-full flex flex-col items-center justify-center px-[8%] py-[6%]">
            {/* Logo */}
            <div className="bg-black rounded-xl px-4 py-2 mb-4">
              <img src={LOGO_URL} alt="Blue Mountain Group" className="h-10 w-auto object-contain" />
            </div>

            <div className="text-[0.65em] uppercase tracking-[0.3em] opacity-60 font-semibold mb-1">
              Blue Mountain Group
            </div>
            <div className="text-[0.65em] uppercase tracking-[0.25em] opacity-50 font-semibold mb-5">
              Certificate of Completion
            </div>

            <div className="text-[1.1em] font-black mb-2">This certifies that</div>

            <div className="font-black italic mb-4" style={{ fontSize: "clamp(28px, 5vw, 52px)", color: "#7C5CFF", lineHeight: 1.1 }}>
              {cert.student_name}
            </div>

            <div className="text-[0.75em] mb-2">has successfully completed</div>

            <div className="font-bold mb-1" style={{ fontSize: "clamp(16px, 2.5vw, 24px)" }}>
              {cert.course_title || cert.course_name}
            </div>

            {cert.grade && (
              <div className="text-[0.65em] opacity-60 mb-6">
                Grade <span className="font-bold" style={{ color: "#7C5CFF" }}>{cert.grade}</span>
              </div>
            )}

            {/* Footer row */}
            <div className="flex items-end justify-between w-full mt-4 px-4">
              <div className="text-left">
                <div className="font-medium italic text-[0.8em]">{cert.instructor || "Lead Instructor"}</div>
                <div className="text-[0.55em] uppercase tracking-widest opacity-50 border-t border-current mt-1 pt-1">Instructor</div>
              </div>

              <div>
                <div className="w-16 h-16 rounded-full border-4 flex items-center justify-center bg-white shadow-lg mx-auto"
                  style={{ borderColor: "#7C5CFF" }}>
                  <Award className="w-8 h-8" style={{ color: "#7C5CFF" }} />
                </div>
              </div>

              <div className="text-right">
                <div className="font-medium italic text-[0.8em]">{fmtDate(cert.issued_at)}</div>
                <div className="text-[0.55em] uppercase tracking-widest opacity-50 border-t border-current mt-1 pt-1">Issue date</div>
              </div>
            </div>

            {/* Cert code */}
            <div className="absolute bottom-[3%] left-1/2 -translate-x-1/2 text-[0.5em] opacity-40 font-mono tracking-widest">
              {cert.code} · verify at {window.location.host}/verify/{cert.id}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-cert { border-radius: 0 !important; box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
